import { EnvelopeIcon, FingerPrintIcon, MapPinIcon, PhoneIcon } from "@heroicons/react/24/solid";
import { Typography } from "@material-tailwind/react";
import { t } from "i18next";

export function formatCustomerAddress(address?: EasyTech.Address): string {
  if (!address) return "";
  const line = [address.address, address.addressCompl].filter(Boolean).join(" ");
  const city = [address.zipCode, address.city].filter(Boolean).join(" ");
  if (line && address.city) return `${line} - ${address.city}`;
  return [line, city].filter(Boolean).join(" - ");
}

export function getCustomerEmail(customer: EasyTech.Customer): string {
  return customer.contactInfo?.email || customer.contactInfo?.mail || t("common:noEmail");
}

export function getCustomerPhone(customer: EasyTech.Customer): string {
  return customer.contactInfo?.phone || t("common:noPhone");
}

export function emptyRecordedData(): EasyTech.CustomerRecordedData {
  return { interventions: 0, visits: 0, contacts: 0, machines: 0 };
}

export function RecordedDataList({
  data
}: {
  data?: EasyTech.CustomerRecordedData
}) {
  const recorded = data || emptyRecordedData();
  const rows = [
    { label: t("admin:customers.recorded.interventions"), value: recorded.interventions },
    { label: t("admin:customers.recorded.visits"), value: recorded.visits },
    { label: t("admin:customers.recorded.contacts"), value: recorded.contacts },
    { label: t("admin:customers.recorded.machines"), value: recorded.machines }
  ];

  return (
    <div className="flex flex-col gap-0.5">
      {rows.map((row) => (
        <Typography key={row.label} variant="small" className="text-blue-gray-700">
          {row.label}: {row.value}
        </Typography>
      ))}
    </div>
  );
}

/**
 * AdminCustomerDisplay: rich customer card used in the merge drawer.
 * The list table reuses the same helpers so details stay consistent.
 */
export default function AdminCustomerDisplay({
  customer
}: {
  customer: EasyTech.Customer
}) {
  const address = formatCustomerAddress(customer.address);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <Typography variant="h6" className="mb-3 uppercase tracking-wide">
        {customer.name}
      </Typography>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-2">
            <FingerPrintIcon className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
            <Typography variant="small">{customer.nip || "—"}</Typography>
          </div>
          {address && (
            <div className="flex items-start gap-2">
              <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
              <Typography variant="small">{address}</Typography>
            </div>
          )}
          <div className="flex items-start gap-2">
            <EnvelopeIcon className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
            <Typography variant="small">{getCustomerEmail(customer)}</Typography>
          </div>
          <div className="flex items-start gap-2">
            <PhoneIcon className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
            <Typography variant="small">{getCustomerPhone(customer)}</Typography>
          </div>
        </div>

        <RecordedDataList data={customer.recordedData} />
      </div>
    </div>
  );
}
